import axios from 'axios';
import { auth } from '../lib/firebase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Interceptor to inject Firebase ID token into admin requests
axios.interceptors.request.use(async (config) => {
    if (config.url && config.url.includes('/api/admin/')) {
        const currentUser = auth.currentUser;
        if (currentUser) {
            try {
                const token = await currentUser.getIdToken();
                config.headers.Authorization = `Bearer ${token}`;
            } catch (err) {
                console.error('Error getting auth token:', err);
            }
        }
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Dashboard stats
export async function fetchDashboardStats(): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/admin/dashboard`);
    return response.data;
}

// Products API
export async function fetchProducts(): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/admin/products`);
    return response.data;
}

export async function createProduct(data: {
    name: string;
    description: string;
    version: string;
    featuresTemplate: Record<string, any>;
}): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/admin/products`, data);
    return response.data;
}

// Licenses API
export async function fetchLicenses(params?: {
    page?: number;
    limit?: number;
    status?: string;
    productId?: string;
    search?: string;
    customerId?: string;
}): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/admin/licenses`, { params });
    return response.data;
}

export async function createLicense(data: {
    licenseKey?: string;
    productId: string;
    customerId?: string;
    serverLimit?: number;
    expiresAt?: number;
    gracePeriodDays?: number;
    features?: Record<string, any>;
}): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/admin/licenses`, data);
    return response.data;
}

export async function updateLicense(licenseKey: string, data: {
    status?: string;
    serverLimit?: number;
    expiresAt?: number | null;
    features?: Record<string, any>;
    customerId?: string;
    gracePeriodDays?: number;
}): Promise<any> {
    const response = await axios.patch(`${API_BASE_URL}/admin/licenses/${licenseKey}`, data);
    return response.data;
}

// Servers API
export async function fetchServers(): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/admin/servers`);
    return response.data;
}

// Customers API
export async function fetchCustomers(): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/admin/customers`);
    return response.data;
}

export async function createCustomer(data: {
    companyName: string;
    contactPerson?: string;
    email: string;
    phone?: string;
    notes?: string;
}): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/admin/customers`, data);
    return response.data;
}

// Renew License API
export async function renewLicense(licenseKey: string, data: {
    duration?: string;
    customExpiry?: number;
    notes?: string;
}): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/admin/licenses/${licenseKey}/renew`, data);
    return response.data;
}

export async function fetchLicenseRenewals(licenseKey: string): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/admin/licenses/${licenseKey}/renewals`);
    return response.data;
}

// Logs API
export async function fetchLogs(params?: { licenseKey?: string; customerId?: string; startAfter?: number; limit?: number }): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/admin/logs`, { params });
    return response.data;
}

// Server Administration API
export async function manageServer(serverId: string, action: string, blockedReason?: string): Promise<any> {
    const response = await axios.patch(`${API_BASE_URL}/admin/servers/${serverId}`, { action, blockedReason });
    return response.data;
}

// Settings API
export async function fetchGlobalSettings(): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/admin/settings`);
    return response.data;
}

export async function saveGlobalSettings(data: { logRetentionDays: number; networkInsights?: { ipInfoToken?: string } }): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/admin/settings`, data);
    return response.data;
}

// Machine Network Insights API
export async function fetchNetworkInsights(): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/admin/network-insights`);
    return response.data;
}

// Test IPInfo Token API
export async function testIpInfoToken(token: string): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/admin/settings/test-token`, { token });
    return response.data;
}

// Load, lazy-load or refresh machine network insights
export async function loadNetworkInsight(data: {
    machineId: string;
    ipAddress: string;
    machineName: string;
    licenseKey: string;
    forceRefresh: boolean;
}): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/admin/network-insights/load`, data);
    return response.data;
}